import apiClient, { STATIC_CONFIG } from './api';

// 专业API服务
export const programService = {
  /**
   * 搜索专业
   * @param {Object} params - 搜索参数
   * @returns {Promise} 专业搜索结果
   */
  searchPrograms: (params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      sortBy: params.sortBy || 'name',
      sortOrder: params.sortOrder || 'asc',
      ...params
    };

    // 过滤掉空值参数
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get('/api/programs/search', { params: filteredParams });
  },

  /**
   * 获取学校的专业列表
   * @param {string} schoolId - 学校ID
   * @param {Object} params - 查询参数
   * @returns {Promise} 专业列表
   */
  getSchoolPrograms: (schoolId, params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get(`/api/schools/${schoolId}/programs`, { params: filteredParams });
  },

  /**
   * 获取专业详情
   * @param {string} programId - 专业ID
   * @returns {Promise} 专业详情
   */
  getProgramDetail: (programId) => {
    return apiClient.get(`/api/programs/${programId}`);
  },

  /**
   * 获取专业类别列表
   * @returns {Promise} 专业类别
   */
  getProgramCategories: () => {
    return apiClient.get('/api/schools/programs/categories');
  },

  /**
   * 获取学位类型列表
   * @returns {Promise} 学位类型
   */
  getDegreeTypes: () => {
    return apiClient.get('/api/schools/degrees');
  },

  /**
   * 获取入学时间选项
   * @returns {Promise} 入学时间选项
   */
  getIntakeOptions: () => {
    return apiClient.get('/api/schools/intakes');
  },

  /**
   * 获取专业统计数据
   * @returns {Promise} 统计数据
   */
  getProgramStats: () => {
    return apiClient.get('/api/schools/programs/stats');
  }
};

// 专业数据转换工具函数
export const programDataMapper = {
  /**
   * 构建完整的图片URL
   * @param {string} imagePath - 图片路径
   * @param {string} fallback - 默认图片路径
   * @param {string} programName - 专业名称（用于生成占位头像）
   * @returns {string} 完整的图片URL
   */
  buildImageUrl: (imagePath, fallback = '/images/programs/default.jpg', programName = '') => {
    // 生成占位头像的辅助函数
    const generateAvatarUrl = (name) => {
      if (!name) return fallback;
      
      const config = STATIC_CONFIG.AVATAR_CONFIG;
      const encodedName = encodeURIComponent(name);
      return `${config.service}?name=${encodedName}&size=${config.size}&background=${config.background}&color=${config.color}&format=${config.format}&rounded=${config.rounded}&bold=${config.bold}`;
    };
    
    // 如果已经是完整URL，直接返回
    if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
      return imagePath;
    }
    
    // 如果有图片路径且启用了baseurl拼接
    if (imagePath && STATIC_CONFIG.ENABLE_IMAGE_BASEURL) {
      // 确保路径以/开头
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      const fullUrl = `${STATIC_CONFIG.IMAGE_BASE_URL}${cleanPath}`;
      
      return fullUrl;
    }
    
    // 如果没有图片路径或未启用baseurl拼接，使用占位头像
    if (programName) {
      const avatarUrl = generateAvatarUrl(programName);

      return avatarUrl;
    }
    
    return fallback;
  },

  /**
   * 映射学位类型（将"专科"转换为"文凭"等）
   * @param {string} degree - 后端返回的学位类型
   * @returns {string} 前端显示的学位类型
   */
  mapAcademicDegree: (degree) => {
    if (!degree || degree === '-') return '学士';
    const degreeMap = {
      '专科': '文凭',
      '本科': '学士',
      '硕士': '硕士',
      '博士': '博士',
      'Diploma': '文凭',
      'Bachelor': '学士',
      'Master': '硕士',
      'Doctor': '博士',
      'PhD': '博士',
      'Doctorate': '博士'
    };
    return degreeMap[degree] || degree;
  },

  /**
   * 将后端专业数据转换为前端格式
   * @param {Object} apiProgram - 后端专业数据
   * @returns {Object} 前端专业数据格式
   */
  mapProgramFromApi: (apiProgram) => {
    // 映射学位类型（局部函数，避免循环引用）
    const mapDegree = (degree) => {
      if (!degree || degree === '-') return '学士';
      const degreeMap = {
        '专科': '文凭',
        '本科': '学士',
        '硕士': '硕士',
        '博士': '博士',
        'Diploma': '文凭',
        'Bachelor': '学士',
        'Master': '硕士',
        'Doctor': '博士',
        'PhD': '博士',
        'Doctorate': '博士'
      };
      return degreeMap[degree] || degree;
    };

    // 解析学制
    const parseDuration = (duration) => {
      if (!duration) return '4年';
      // 确保返回带年的格式
      return duration.toString().includes('年') ? duration : `${duration}年`;
    };

    // 解析学费
    const parseFees = (apiProgram) => {
      // 优先使用 fees_tuition，然后是 TuitionFee，最后是 fees_total
      const totalFee = apiProgram.fees_tuition ?? 
                      apiProgram.feesTuition ?? 
                      apiProgram.TuitionFee ?? 
                      apiProgram.tuitionFee?.international ?? 
                      apiProgram.feesTotal ?? 
                      apiProgram.fees_total ?? 
                      null;
      
      // 转换为数字
      let feeNumber = null;
      if (totalFee !== null && totalFee !== undefined && totalFee !== '') {
        feeNumber = typeof totalFee === 'number' ? totalFee : parseFloat(totalFee);
        // 如果解析失败或结果为NaN，设置为null
        if (isNaN(feeNumber) || feeNumber <= 0) {
          feeNumber = null;
        }
      }
      
      return {
        local: feeNumber ? feeNumber * 0.8 : null, // 假设本地学生有20%折扣
        international: feeNumber,
        currency: apiProgram.currency || 'MYR'
      };
    };

    // 解析入学时间
    const parseIntakeMonths = (intakes) => {
      if (!intakes) return ['-'];
      const monthMap = {
        '1': '一月', '2': '二月', '3': '三月',
        '4': '四月', '5': '五月', '6': '六月',
        '7': '七月', '8': '八月', '9': '九月',
        '10': '十月', '11': '十一月', '12': '十二月',
        '全年': '全年'
      };
      let arr = [];
      if (Array.isArray(intakes)) {
        arr = intakes;
      } else if (typeof intakes === 'string') {
        // 支持 1.2.9、1月,9月、1,2,9、1/2/9 等
        arr = intakes.split(/[.,，\/\s]+/);
      }
      return arr
        .map(month => {
          if (!month) return null;
          let m = month.toString().replace(/月/, '').trim();
          // 兼容 "1月" 这种
          if (monthMap[m]) return monthMap[m];
          // 兼容 "9月" 直接返回
          if (/^\d{1,2}月$/.test(month)) return monthMap[m] || m + '月';
          // 兼容中文
          if (Object.values(monthMap).includes(month)) return month;
          return month;
        })
        .filter(Boolean);
    };

    // 解析课程列表
    const parseCourses = (coursesText) => {
      if (!coursesText) return [];
      if (Array.isArray(coursesText)) return coursesText;
      
      // 将<br>标签转换为换行符，然后分割
      const textWithLineBreaks = coursesText
        .replace(/<br\s*\/?>/gi, '\n')  // 将<br>转换为换行符
        .replace(/<\/p>/gi, '\n')       // 将</p>转换为换行符
        .replace(/<p[^>]*>/gi, '');     // 移除<p>标签
      
      // 按换行符或分号分割
      const courses = textWithLineBreaks
        .split(/[\n;]/)
        .map(course => course.trim())
        .filter(course => course.length > 0);
      
      // 检查是否已经包含数字索引，如果包含则不添加
      const hasExistingIndex = courses.some(course => /^\d+\./.test(course));
      
      if (hasExistingIndex) {
        return courses; // 如果已有索引，直接返回
      } else {
        return courses.map((course, index) => `${index + 1}. ${course}`); // 如果没有索引，添加索引
      }
    };

    return {
      id: apiProgram.id,
      name: apiProgram.name_cn || apiProgram.CnName || apiProgram.name || '',
      nameEn: apiProgram.name_en || apiProgram.EnName || apiProgram.nameEn || '',
      category: apiProgram.category || apiProgram.faculty_name_cn || apiProgram.FacultyCnName || '-',
      level: mapDegree(apiProgram.academic_degree || apiProgram.AcademicDegree || apiProgram.level || '-'),
      mode: apiProgram.mode || '课程型',
      duration: parseDuration(apiProgram.duration || apiProgram.Duration),
      language: apiProgram.language || '英语',
      tuitionFee: parseFees(apiProgram).international ?? null,
      miscellaneousFee: apiProgram.feesMisc || '-',
      totalFee: parseFloat(apiProgram.feesTotal) || 0,
      currency: apiProgram.currency || 'MYR',
      university: {
        id: apiProgram.uni_id || apiProgram.Sid || apiProgram.university?.id || 'unknown',
        sid: apiProgram.university_s_id || apiProgram.Uid || apiProgram.university?.sid || 'unknown',
        name: apiProgram.uni_name_cn || apiProgram.UniCnName || apiProgram.university?.name || '未知院校',
        nameEn: apiProgram.uni_name_en || apiProgram.UniEnName || apiProgram.university?.nameEn || 'Unknown University',
        logo: apiProgram.university_logo || apiProgram.Image || apiProgram.university?.logo || '/images/university/default.jpg',
        ranking: apiProgram.university_ranking || apiProgram.UniversityRanking || apiProgram.university?.ranking || '-'
      },
      faculty: apiProgram.faculty_name_cn || apiProgram.FacultyCnName || apiProgram.faculty?.name || '学院信息',
      location: apiProgram.campus_name_cn || apiProgram.campus?.name || '',
      description: apiProgram.description || 
                  `${apiProgram.name_cn || apiProgram.CnName || apiProgram.name || '专业'}，提供优质的教育和培训。`,
      requirements: {
        academic: apiProgram.requirements?.academic || '相关学历背景，成绩优良',
        language: apiProgram.requirements?.language || 'IELTS 6.0 或 TOEFL 550',
        gpa: apiProgram.cgpa || '-',
        experience: apiProgram.requirements?.experience || '无特殊工作经验要求',
        documents: apiProgram.requirements?.documents || ['学历证书', '成绩单', '语言成绩', '个人陈述', '推荐信']
      },
      curriculum: parseCourses(apiProgram.curriculum),
      electiveCourses: parseCourses(apiProgram.electiveCoursesCn),
      interview: apiProgram.isInterview ? '是' : '否',
      portfolio: apiProgram.isPortfolio || false,
      crossMajor: apiProgram.isInterdisciplinary || false,
      credits: apiProgram.credits || '-',
      semesters: {
        min: apiProgram.shortestTerm || '-',
        max: apiProgram.longestTerm || '-'
      },
      intakeMonths: parseIntakeMonths(apiProgram.intakes || apiProgram.Intakes || apiProgram.intakeMonths),
      scholarship: apiProgram.scholarship || false,
      scholarshipAmount: apiProgram.scholarshipAmount || 0,
      tags: apiProgram.tags || ['专业认证'],
      featured: apiProgram.featured || false,
      image: programDataMapper.buildImageUrl(apiProgram.university_logo || apiProgram.Image || apiProgram.image, '/images/programs/default.jpg', apiProgram.name_cn || apiProgram.CnName || apiProgram.name)
    };
  },

  /**
   * 将前端筛选参数转换为后端API参数
   * @param {Object} filters - 前端筛选参数
   * @param {string} searchTerm - 搜索关键词
   * @returns {Object} 后端API参数
   */
  mapFiltersToApiParams: (filters, searchTerm = '') => {
    const params = {};

    // 搜索关键词
    if (searchTerm) {
      params.keyword = searchTerm;
    }

    // 学位级别
    if (filters.level) {
      params.academicDegree = filters.level;
    }

    // 学制
    if (filters.duration) {
      // 提取数字部分
      const durationNumber = filters.duration.replace(/[^\d.]/g, '');
      params.duration = durationNumber;
    }

    // 入学时间
    if (filters.intake) {
      // 将中文月份转换为数字
      const monthMap = {
        '一月': '1', '二月': '2', '三月': '3',
        '四月': '4', '五月': '5', '六月': '6',
        '七月': '7', '八月': '8', '九月': '9',
        '十月': '10', '十一月': '11', '十二月': '12'
      };
      params.intakes = monthMap[filters.intake] || filters.intake;
    }

    // 学费范围
    if (filters.tuitionRange && filters.tuitionRange.length === 2) {
      params.min_fees_total = filters.tuitionRange[0].toString();
      params.max_fees_total = filters.tuitionRange[1].toString();
    }

    // 模式筛选
    if (filters.mode) {
      params.mode = filters.mode;
    }

    return params;
  },

  /**
   * 将前端排序参数转换为后端API参数
   * @param {string} sortBy - 前端排序字段
   * @returns {Object} 后端排序参数
   */
  mapSortToApiParams: (sortBy) => {
    const sortMap = {
      'tuition-low': { sortBy: 'fees_total', sortOrder: 'asc' },
      'tuition-high': { sortBy: 'fees_total', sortOrder: 'desc' },
      'name': { sortBy: 'name_cn', sortOrder: 'asc' }
    };

    return sortMap[sortBy] || { sortBy: 'name_cn', sortOrder: 'asc' };
  }
}; 